"use client"

import React, { useEffect, useState, useContext, useRef } from "react"
import { useParams, Link } from "react-router-dom"
import axios from "axios"
import { motion } from "framer-motion"
import { CheckCircle2, Trophy, ArrowRight, Download, IdCard } from "lucide-react"
import { CircuitoContext } from "../../context/CircuitoContext"
import { DomainContext } from "./LayoutCircuito"
import "../../styles/circuitoDesignSystem.css"

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

// Funções auxiliares para carregar imagens
const carregarImagem = (src) => new Promise((resolve, reject) => {
  const img = new Image(); 
  img.onload = () => resolve(img); 
  img.onerror = reject; 
  img.src = src;
})

const carregarImagemCross = (src) => new Promise((resolve, reject) => {
  const img = new Image(); 
  img.crossOrigin = "anonymous"; 
  img.onload = () => resolve(img); 
  img.onerror = reject; 
  img.src = src;
})

export default function CircuitoInscricaoSucesso() {
  const { pedidoId } = useParams()
  const { circuito } = useContext(CircuitoContext)
  const { basePath } = useContext(DomainContext)
  
  const [status, setStatus] = useState("pendente")
  const [loading, setLoading] = useState(true)
  const [baixandoPdf, setBaixandoPdf] = useState(false)
  
  // 💡 Novos estados e referências para a Carteirinha
  const [inscricao, setInscricao] = useState(null) 
  const [gerandoCarteirinha, setGerandoCarteirinha] = useState(false)
  const canvasRef = useRef(null)

  useEffect(() => {
    if (!pedidoId) return;

    const checarStatus = async () => {
      try {
        const res = await axios.get(`${API}/circuitos/pedidos/${pedidoId}/status`);
        setStatus(res.data.status);
        
        // 💡 Armazena os dados da inscrição (incluindo a foto) retornados pelo backend
        if (res.data.inscricao) {
          setInscricao(res.data.inscricao);
        }
      } catch (err) {
        console.error("Erro ao buscar pedido");
      } finally {
        setLoading(false);
      }
    };

    checarStatus();
    const interval = setInterval(() => { if (status !== "pago") checarStatus(); }, 3000);
    return () => clearInterval(interval);
  }, [pedidoId, status]);

  const baixarComprovante = async () => {
    setBaixandoPdf(true);
    try {
      const res = await axios.get(`${API}/circuitos/pedidos/${pedidoId}/comprovante`, {
        responseType: 'blob' 
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comprovante-${circuito?.nome || 'Inscricao'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Erro ao baixar comprovante", error);
      alert("Erro ao baixar o comprovante. Tente novamente.");
    } finally {
      setBaixandoPdf(false);
    }
  };

  // 💡 FUNÇÃO MÁGICA QUE GERA A CARTEIRINHA NO CANVAS E BAIXA
  const gerarEBaixarCarteirinha = async () => {
    if (!inscricao || !inscricao.fotoVaqueiro) {
      return alert("Nenhuma foto foi enviada durante a inscrição para gerar a carteirinha.");
    }
    
    setGerandoCarteirinha(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    // Tamanho padrão (Story Instagram)
    canvas.width = 1080;
    canvas.height = 1920;

    try {
      // 1. Fundo
      const templateImg = await carregarImagem("/template-carteinha.jpg");
      ctx.drawImage(templateImg, 0, 0, canvas.width, canvas.height);

      // 2. Área da foto e lógica de Object-Fit Cover
      const photoBoxX = 105, photoBoxY = 342, photoBoxW = 856, photoBoxH = 1048;
      const userImg = await carregarImagem(inscricao.fotoVaqueiro);
      
      ctx.save();
      ctx.beginPath();
      ctx.rect(photoBoxX, photoBoxY, photoBoxW, photoBoxH);
      ctx.clip(); 

      const imgRatio = userImg.width / userImg.height;
      const boxRatio = photoBoxW / photoBoxH;
      let renderW, renderH, renderX, renderY;

      if (imgRatio > boxRatio) {
        renderH = photoBoxH; 
        renderW = userImg.width * (photoBoxH / userImg.height);
        renderX = photoBoxX - (renderW - photoBoxW) / 2; 
        renderY = photoBoxY;
      } else {
        renderW = photoBoxW; 
        renderH = userImg.height * (photoBoxW / userImg.width);
        renderX = photoBoxX; 
        renderY = photoBoxY - (renderH - photoBoxH) / 2;
      }
      
      ctx.drawImage(userImg, renderX, renderY, renderW, renderH);
      ctx.restore(); 

      // 3. Overlay (Camada de cima)
      const overlayImg = await carregarImagem("/cima.png");
      const overlayW = 590, overlayH = 400;
      const overlayX = (canvas.width - overlayW) / 2;
      const overlayY = 1430 - (overlayH / 2) - 25; 
      ctx.drawImage(overlayImg, overlayX, overlayY, overlayW, overlayH);

      // 4. Textos
      ctx.textAlign = "center";
      
      ctx.font = "bold 60px Impact, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.fillText(inscricao.categoria.toUpperCase(), canvas.width / 2, 1420);
      
      ctx.font = "bold 50px Arial, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.fillText(inscricao.nomeVaqueiro.toUpperCase(), canvas.width / 2, 1550);

      const isCustomDomain = window.location.hostname !== "ticketdovaqueiro.com" && window.location.hostname !== "www.ticketdovaqueiro.com" && window.location.hostname !== "localhost";
      const urlSite = isCustomDomain ? window.location.hostname.toUpperCase() : `TICKETDOVAQUEIRO.COM/CIRCUITO/${circuito?.slug.toUpperCase()}`;
      ctx.font = "italic bold 38px Arial, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.fillText(urlSite, canvas.width / 2, 1790);

      // 5. Logo do Circuito
      if (circuito?.imagem) {
        const logoUrl = `https://api.ticketdovaqueiro.com/uploads/${circuito.imagem}`;
        const logoImg = await carregarImagemCross(logoUrl);
        const logoBoxX = 300, logoBoxY = 26, logoBoxW = 415, logoBoxH = 300;
        const lRatio = logoImg.width / logoImg.height;
        let lRenderW = logoBoxW, lRenderH = logoBoxH;

        if (lRatio > 1) { 
          lRenderH = logoBoxW / lRatio; 
        } else { 
          lRenderW = logoBoxH * lRatio; 
        }
        
        const lRenderX = logoBoxX + (logoBoxW - lRenderW) / 2;
        const lRenderY = logoBoxY + (logoBoxH - lRenderH) / 2;
        ctx.drawImage(logoImg, lRenderX, lRenderY, lRenderW, lRenderH);
      }

      // 6. Faz o download automático da imagem
      const urlFinal = canvas.toDataURL("image/jpeg", 0.95);
      const link = document.createElement('a');
      link.download = `Carteirinha-${inscricao.nomeVaqueiro.replace(/\s+/g, '')}.jpg`;
      link.href = urlFinal;
      link.click();

    } catch (e) {
      console.error(e);
      alert("Erro ao processar as imagens da carteirinha.");
    } finally {
      setGerandoCarteirinha(false);
    }
  }

  if (loading) {
    return <div className="circuito-shell"><div className="loading-spinner" style={{ margin: "100px auto" }}></div></div>;
  }

  return (
    <div className="circuito-shell">
      {/* 💡 CANVAS ESCONDIDO PARA DESENHAR A CARTEIRINHA */}
      <canvas ref={canvasRef} style={{ display: "none" }}></canvas>

      <div style={{ maxWidth: 500, margin: "60px auto", padding: "0 16px" }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ background: "var(--c-card)", borderRadius: "var(--card-radius)", padding: "40px 32px", border: "var(--card-border)", boxShadow: "0 12px 40px rgba(0,0,0,0.4)", textAlign: "center", position: "relative", overflow: "hidden" }}>
          {status === "pago" && (<div style={{ position: "absolute", top: "-50px", left: "50%", transform: "translateX(-50%)", width: "200px", height: "200px", background: "#10b981", filter: "blur(120px)", opacity: 0.2, pointerEvents: "none" }} />)}

          {status === "pago" ? (
            <>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} style={{ display: "inline-flex", background: "rgba(16, 185, 129, 0.15)", padding: 20, borderRadius: "50%", marginBottom: 24 }}>
                <CheckCircle2 size={64} color="#10b981" />
              </motion.div>
              <h1 style={{ fontSize: "28px", fontWeight: 800, color: "#fff", marginBottom: 8 }}>Pagamento <span style={{ color: "#10b981" }}>Aprovado!</span></h1>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", lineHeight: "1.6", marginBottom: 32 }}>Parabéns! Sua associação ao circuito <strong>{circuito?.nome}</strong> foi confirmada com sucesso.</p>
              
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 16, marginBottom: 32, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                 <Trophy size={24} color="var(--c-accent)" />
                 <span style={{ fontSize: "14px", color: "#fff", fontWeight: 600 }}>Boa sorte nas pistas, Vaqueiro!</span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "32px" }}>
                {/* BOTÃO DE BAIXAR COMPROVANTE (Vazado/Outline) */}
                <button 
                  onClick={baixarComprovante} 
                  disabled={baixandoPdf}
                  style={{ 
                    width: "100%", height: "56px", fontSize: "15px", fontWeight: 700, 
                    display: "flex", justifyContent: "center", alignItems: "center", gap: 8, 
                    background: "transparent", color: "var(--c-accent)", 
                    border: "2px solid var(--c-accent)", borderRadius: "12px", 
                    cursor: baixandoPdf ? "not-allowed" : "pointer",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => !baixandoPdf && (e.currentTarget.style.background = "rgba(212, 175, 55, 0.1)")}
                  onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <Download size={20} />
                  {baixandoPdf ? "Gerando PDF..." : "Ver Comprovante"}
                </button>

                {/* 💡 NOVO BOTÃO DA CARTEIRINHA MÁGICA */}
                <button 
                  onClick={gerarEBaixarCarteirinha} 
                  disabled={gerandoCarteirinha || !inscricao?.fotoVaqueiro}
                  className="c-btn c-btn--primary"
                  style={{ 
                    width: "100%", height: "56px", fontSize: "15px", fontWeight: 700, 
                    display: "flex", justifyContent: "center", alignItems: "center", gap: 8, 
                    borderRadius: "12px",
                    opacity: (!inscricao?.fotoVaqueiro) ? 0.6 : 1,
                    cursor: (!inscricao?.fotoVaqueiro || gerandoCarteirinha) ? "not-allowed" : "pointer"
                  }}
                  title={!inscricao?.fotoVaqueiro ? "Nenhuma foto foi enviada na inscrição" : ""}
                >
                  <IdCard size={20} />
                  {gerandoCarteirinha ? "Gerando Imagem HD..." : "Baixar Carteirinha Digital"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "inline-flex", background: "rgba(245, 158, 11, 0.15)", padding: 20, borderRadius: "50%", marginBottom: 24 }}>
                <div className="loading-spinner" style={{ width: 48, height: 48, borderTopColor: "#f59e0b" }}></div>
              </div>
              <h1 style={{ fontSize: "28px", fontWeight: 800, color: "#fff", marginBottom: 8 }}>Processando...</h1>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", lineHeight: "1.6", marginBottom: 32 }}>Estamos aguardando a confirmação do banco. Assim que compensar, sua inscrição será ativada.</p>
            </>
          )}

          <Link to={`${basePath}/`} style={{ textDecoration: "none", width: "100%", display: "block" }}>
            <button className="c-btn" style={{ width: "100%", height: "56px", fontSize: "16px", fontWeight: 700, display: "flex", justifyContent: "center", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", borderRadius: "12px" }}>
              Voltar ao Início <ArrowRight size={20} />
            </button>
          </Link>
        </motion.div>
      </div>
    </div>
  )
}